// Pricing route (public storefront). Keep keys in sync with pl/pricing.ts.
// Prices/gem counts come from the billing API (GET /api/billing/plans + /topups)
// — only marketing copy lives here.
//
// CREDIBILITY PASS (P06): see the note in pl/pricing.ts. Removed "4K export"
// (the renderer outputs 1080×1920), "Priority generation" / "Priority support" /
// "Team access" (nothing implements them), the wrong slot counts, and
// "1 gem = 1 edit" (an edit costs 3 gems, SPEC A5).
export const pricing = {
  hero: {
    // Canvas 3 breaks the headline in two — the second line is the one that
    // explains the model.
    titleOne: 'Subscription for access, tokens for the work.',
    title: 'You pay for access',
    title2: 'and for the gems you pay the work with.',
    sub: 'A subscription gives you access to the software and a monthly pool of gems. You spend gems on specific actions - the rest of the time they cost you nothing.',
  },
  signedIn: {
    as: 'Signed in as {email}',
    gemsEdits: 'You have {gems} = {edits}.',
    gems: 'You have {gems}.',
    next: 'A plan is your next step - a pool of gems every month.',
    toBilling: 'Go to Billing',
  },
  switch: { label: 'Pick a plan to compare' },
  diff: {
    heading: 'How they differ',
    gemsPerMonth: 'gems / mo',
    edits: '≈ edits',
    accounts: 'publishing accounts',
    storage: 'storage',
    price: 'price / mo',
  },
  grossNote: 'Prices include VAT',
  salesPaused: 'sales paused',
  // The per-action price list. Rule 34 (B-R3): the heading no longer names the
  // currency, because the three rows under it do ("Edit 1 token"). The captions
  // under each action went out with principle 8.
  gemCost: {
    heading: 'What it costs',
    render: 'Edit',
    publish: 'Publishing',
    cut: 'Cutting footage',
    unavailable: 'The per-action prices are unavailable for a moment - the numbers appear once the server answers.',
  },
  cycle: {
    monthly: 'billed monthly · prices include VAT',
  },
  state: {
    loading: 'Loading pricing…',
    error: 'Couldn’t load pricing.',
    retry: 'Try again',
  },
  plans: {
    heading: 'Plans',
    perMonth: '/mo',
    // The pool row on a plan card: "500 tokens every month". `tokenWord` inflects
    // the unit, so only the qualifier lives here — never a conversion into edits.
    poolPerMonth: 'every month',
    gemsLabel: 'gems / mo',
    // --- Plan cards built FROM API DATA (canvas 3). The `starter/pro/max` keys
    // below describe today's hand-written page and disappear together with P01's
    // Pricing.tsx rewrite. These are for the data-driven version.
    mostPopular: 'Most popular',
    gemsPerMonth: 'gems per month',
    // Polish inflects this unit three ways; English keeps the key set identical.
    accounts_one: 'publishing account',
    accounts_few: 'publishing accounts',
    accounts_many: 'publishing accounts',
    storage: 'for your footage',
    includesPrev: 'Everything in {plan}',
    topupHint: 'Extra gems:',
    topupHintMax: 'Cheaper top-ups:',
    // {edits} and {published} arrive ALREADY INFLECTED from the component
    // (editWord()), because Polish needs two different forms in one sentence.
    equivalence: '{gems} gems ≈ {edits} or {published} with publishing',
    cta: 'Choose {plan}',
    starter: {
      name: 'Starter',
      desc: 'For an artist running a single account.',
      cta: 'Choose Starter',
      f1: '1 publishing account',
      f2: 'Generator, clip packs, lyrics editor',
      f3: '9:16 edits in 1080p, yours to download',
      f4: 'Lyrics synced to the syllable',
      f5: '25 GB for your footage',
      f6: 'Buy extra gems anytime',
    },
    pro: {
      name: 'Pro',
      badge: 'Most popular',
      desc: 'For an artist working several accounts at once.',
      cta: 'Choose Pro',
      f1: '8 publishing accounts',
      f2: 'Everything in Starter',
      f3: 'Autopilot - spreads posts across accounts',
      f4: 'Your own footage cut into clips',
      f5: '100 GB for your footage',
      f6: 'Buy extra gems and slots',
    },
    max: {
      name: 'Max',
      desc: 'For labels, agencies and managers running many artists.',
      cta: 'Choose Max',
      f1: '40 publishing accounts',
      f2: 'Everything in Pro',
      f3: 'Many artists in one dashboard',
      f4: '500 GB for your footage',
      f5: 'Cheaper gem top-ups',
      f6: 'Gem bundles up to 5,000',
    },
  },
  // The free start under the plans (38-R3): it works INDEPENDENTLY of whether the
  // shop is open, so with sales paused it is the page's only live action, and with
  // sales open it drops to a small quiet button beside the microcopy.
  freeStart: { cta: 'Start free' },
  guarantee: {
    // Same sentence as landing.hero.micro; {n} and {edits} come from FREE_START_EDITS.
    freeEdits: 'No card · {n} free {edits}',
    cancel: 'Cancel yourself, online - no need to email us',
    publishing: 'Publishing to TikTok, Instagram and YouTube through the official APIs',
  },
  // Top-ups as their own section — somebody who ran out mid-month should find
  // the answer without walking back through the plan comparison.
  addons: {
    heading: 'When you run out mid-month',
    headingShort: 'Token packs',
    validForLabel: 'valid for 24 months',
    buy: 'Buy',
    validFor: 'Purchased gems are valid for 24 months from purchase - they stay on your account after the plan renews.',
    gems: 'gems',
    accounts: 'publishing accounts',
    maxOnly: 'Max plan only',
    maxPrice: '{price} on Max',
    perMonth: 'per month, on top of your plan',
    equiv: '≈ {edits}',
  },
  // The three things a customer asks BEFORE paying, not after. The card
  // descriptor is here deliberately: an unrecognised charge is a chargeback,
  // not an email.
  trust: {
    heading: 'Before you pay',
    factCancel: 'You cancel in the customer portal; access stays until the end of the period.',
    factDescriptor: 'On your card statement: EDIMI.',
    factRefund: 'A failed render: the tokens come back automatically.',
    refundsShort: 'Refunds',
    cancelTitle: 'Cancel yourself, online',
    cancelBody: 'Billing, “Manage payment”, customer portal. Access stays until the end of the paid period.',
    descriptorTitle: 'On your statement: EDIMI',
    descriptorBody: 'That is how the charge appears on your card - so nobody reports it as unknown.',
    refundTitle: 'A failed render costs nothing',
    refundBody: 'Gems come back automatically - including for a post that did not go out.',
    refundsLink: 'Refunds & cancellation',
    sellerLine: '14-day withdrawal with pro-rata settlement · seller: JDG Dominik Łupicki · payment: Stripe',
  },
  topup: {
    eyebrow: 'Top-ups',
    title: 'Out of gems? Top up.',
    lead: 'One-off gem bundles - valid for 24 months from purchase, they stay on your account after the plan renews. The largest bundles are available on Max.',
    gems: 'gems',
    bestBadge: 'Best value',
  },
  pay: {
    stripe: 'Stripe',
  },
  cta: {
    title: 'Questions about the plans?',
    lead: 'Check the most common questions or just start for free.',
    faq: 'Go to FAQ',
  },
}
